/**
 * Feature Context
 *
 * Provides company feature access management for the frontend:
 * - Loads company's enabled features from backend
 * - Provides feature checking functions
 * - Tracks feature usage limits
 * - Auto-refreshes on user/company change
 */

import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

interface FeatureLimit {
  allowed: boolean;
  remaining: number;
  total: number | null;
  period: string | null;
}

interface FeatureInfo {
  feature: string;
  enabled: boolean;
  limit: FeatureLimit | null;
}

interface FeatureContextType {
  features: Record<string, FeatureInfo>;
  loading: boolean;
  hasFeature: (feature: string) => boolean;
  getFeatureLimit: (feature: string) => FeatureLimit | null;
  canUseFeature: (feature: string) => boolean;
  refreshFeatures: () => Promise<void>;
}

const FeatureContext = createContext<FeatureContextType | undefined>(undefined);

/**
 * Feature Provider Component
 * Wraps the app to provide feature access context
 */
export const FeatureProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [features, setFeatures] = useState<Record<string, FeatureInfo>>({});
  const [loading, setLoading] = useState(true);

  /**
   * Load company features from backend
   */
  const loadFeatures = async () => {
    if (!user || !isAuthenticated || !user.company_id) {
      setFeatures({});
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get(`/admin/features/companies/${user.company_id}`) as unknown as {
        features: Array<{
          feature_key: string;
          enabled: boolean;
          limit_value: number | null;
          limit_period: string | null;
          usage: { usage_count: number };
        }>;
      };

      if (response.features) {
        // Convert array to object for easy lookup
        const featureMap: Record<string, FeatureInfo> = {};

        response.features.forEach((feature: {
          feature_key: string;
          enabled: boolean;
          limit_value: number | null;
          limit_period: string | null;
          usage: { usage_count: number };
        }) => {
          const remaining = feature.limit_value
            ? Math.max(0, feature.limit_value - (feature.usage?.usage_count || 0))
            : -1; // -1 = unlimited

          featureMap[feature.feature_key] = {
            feature: feature.feature_key,
            enabled: feature.enabled,
            limit: feature.limit_value ? {
              allowed: remaining > 0,
              remaining,
              total: feature.limit_value,
              period: feature.limit_period,
            } : null,
          };
        });

        setFeatures(featureMap);
      }
    } catch (error) {
      console.error('Failed to load features:', error);
      setFeatures({});
    } finally {
      setLoading(false);
    }
  };

  /**
   * Refresh features when user or company changes
   */
  useEffect(() => {
    loadFeatures();
  }, [user?.id, user?.company_id, isAuthenticated]);

  /**
   * Check if company has a specific feature enabled
   */
  const hasFeature = (feature: string): boolean => {
    return features[feature]?.enabled || false;
  };

  /**
   * Get feature usage limit info
   */
  const getFeatureLimit = (feature: string): FeatureLimit | null => {
    return features[feature]?.limit || null;
  };

  /**
   * Check if feature can be used (enabled + within limits)
   */
  const canUseFeature = (feature: string): boolean => {
    const featureInfo = features[feature];
    if (!featureInfo || !featureInfo.enabled) {
      return false;
    }

    // If no limit, can use
    if (!featureInfo.limit) {
      return true;
    }

    // Check if within limit
    return featureInfo.limit.allowed;
  };

  /**
   * Manually refresh features (e.g., after feature change or usage)
   */
  const refreshFeatures = async () => {
    await loadFeatures();
  };

  return (
    <FeatureContext.Provider
      value={{
        features,
        loading,
        hasFeature,
        getFeatureLimit,
        canUseFeature,
        refreshFeatures,
      }}
    >
      {children}
    </FeatureContext.Provider>
  );
};

/**
 * Hook to access feature context
 * Must be used within FeatureProvider
 */
export const useFeatures = () => {
  const context = useContext(FeatureContext);
  if (context === undefined) {
    throw new Error('useFeatures must be used within a FeatureProvider');
  }
  return context;
};
